import React from "react";
import {Box} from "@mui/material";
import {printoutPalette} from "../../core/printoutPalette";
import {printoutRule, printoutTheme, printoutTypeSx} from "../../core/printoutTheme";
import type {
    DataListAlign,
    DataListColumn,
    DataListTextStyle,
} from "./printoutDataListOptions";

// Description columns receive more width; other measures come from the print theme.
const WIDE_COLUMN_KEYS = new Set([
    "folioItemDescription",
    "folioItemPriceDescription",
    "folioItemCategory",
    "resRestaurantName",
    "resResourceNames",
    "resFacilityNames",
    "resGuestBreakdown",
    "guestFullName",
    "guestAllergies",
    "guestDietaryPreferences",
    "agendaItemName",
    "agendaItemLocation",
]);

const cellJustify = (align: DataListAlign) => (
    align === "right" ? "flex-end" : align === "center" ? "center" : "flex-start"
);

// Bold is additive so unformatted headers retain their base weight.
const formatSx = (style: DataListTextStyle) => ({
    fontStyle: style.italic ? "italic" : "normal",
    ...(style.bold ? {fontWeight: 700} : {}),
});

interface Props {
    columns: DataListColumn[];
    rowCount: number;
    rowGroupLabels?: string[];
    renderCell: (column: DataListColumn, rowIndex: number) => React.ReactNode;
    emptyText?: string;
    title?: string;
}

const ListTitle = ({title}: {title?: string}) => title ? (
    <Box sx={{color: printoutPalette.primaryText, ...printoutTypeSx(printoutTheme.type.h3), pb: "4mm"}}>
        {title}
    </Box>
) : null;

const EmptyList = ({emptyText}: Pick<Props, "emptyText">) => {
    if (!emptyText) return null;

    return (
        <Box
            sx={{
                color: printoutPalette.mutedText,
                ...printoutTypeSx(printoutTheme.type.tableValue),
                fontStyle: "italic",
                py: `${printoutTheme.table.rowPaddingY}px`,
            }}
        >
            {emptyText}
        </Box>
    );
};

const DataListView = (props: Props) => {
    const {columns, emptyText, renderCell, rowCount, rowGroupLabels, title} = props;

    const gridTemplateColumns = columns
        .map(column => WIDE_COLUMN_KEYS.has(column.variableKey) ? "minmax(0, 3fr)" : "minmax(0, 1fr)")
        .join(" ");
    return (
        <Box sx={{color: printoutPalette.secondaryText, display: "flex", flexDirection: "column", width: "100%"}}>
            <ListTitle title={title}/>
            <Box
                sx={{
                    alignItems: "end",
                    borderBottom: printoutRule("default"),
                    color: printoutPalette.secondaryText,
                    columnGap: `${printoutTheme.table.columnGap}px`,
                    display: "grid",
                    ...printoutTypeSx(printoutTheme.type.tableValue),
                    fontWeight: 600,
                    gridTemplateColumns,
                    pb: `${printoutTheme.table.headerPaddingBottom}px`,
                    width: "100%",
                }}
            >
                {columns.map(column => (
                    <Box
                        key={column.id}
                        sx={{
                            display: "flex",
                            justifyContent: cellJustify(column.align),
                            minWidth: 0,
                            ...formatSx(column.headerFormat),
                        }}
                    >
                        {column.label || column.variableKey}
                    </Box>
                ))}
            </Box>

            {rowCount === 0 ? (
                <EmptyList emptyText={emptyText}/>
            ) : Array.from({length: rowCount}).map((_, rowIndex) => (
                <React.Fragment key={rowIndex}>
                    {rowGroupLabels?.[rowIndex] && (
                        <Box
                            sx={{
                                ...printoutTypeSx(printoutTheme.type.tableValue),
                                breakAfter: "avoid",
                                color: printoutPalette.primaryText,
                                fontWeight: 600,
                                pt: rowIndex === 0 ? "4mm" : "6mm",
                                pb: "2mm",
                            }}
                        >
                            {rowGroupLabels[rowIndex]}
                        </Box>
                    )}
                    <Box
                        sx={{
                            alignItems: "start",
                            borderBottom: printoutRule("light"),
                            breakInside: "avoid",
                            columnGap: `${printoutTheme.table.columnGap}px`,
                            display: "grid",
                            ...printoutTypeSx(printoutTheme.type.tableValue),
                            gridTemplateColumns,
                            py: `${printoutTheme.table.rowPaddingY}px`,
                            width: "100%",
                        }}
                    >
                        {columns.map(column => (
                            <Box
                                key={column.id}
                                sx={{
                                    color: column.cellFormat.bold ? printoutPalette.primaryText : undefined,
                                    display: "flex",
                                    justifyContent: cellJustify(column.align),
                                    minWidth: 0,
                                    overflowWrap: "anywhere",
                                    ...formatSx(column.cellFormat),
                                }}
                            >
                                {renderCell(column, rowIndex)}
                            </Box>
                        ))}
                    </Box>
                </React.Fragment>
            ))}
        </Box>
    );
};

export default DataListView;
